import { Controller } from '@hotwired/stimulus'
import I18n from 'i18n-js/index.js.erb';

// Real image format, detected from the file's leading bytes rather than its (spoofable) extension/file.type.
const IMAGE_SIGNATURES: Record<string, number[]> = {
  'image/png': [0x89, 0x50, 0x4E, 0x47],
  'image/jpeg': [0xFF, 0xD8, 0xFF]
};

export default class extends Controller {
  static targets = [
    'fullscreenButton',
    'answerForm',
    'uploadImageButton',
    'imageInput',
    'dragHelpText',
    'resizeHelpText',
    'unsupportedFormatHelpText',
    'uploadPdfButton',
    'pdfInput',
    'pdfFileName',
    'removePdfButton',
    'removePdfFlag',
    'cropX',
    'cropY',
    'cropW',
    'cropH'
  ]

  declare fullscreenButtonTarget: HTMLButtonElement;
  declare answerFormTarget: HTMLFormElement;
  declare uploadImageButtonTarget: HTMLButtonElement;
  declare imageInputTarget: HTMLImageElement;
  declare dragHelpTextTarget: HTMLElement;
  declare resizeHelpTextTarget: HTMLElement;
  declare unsupportedFormatHelpTextTarget: HTMLElement;
  declare uploadPdfButtonTarget: HTMLButtonElement;
  declare pdfInputTarget: HTMLInputElement;
  declare pdfFileNameTarget: HTMLElement;
  declare removePdfButtonTarget: HTMLElement;
  declare removePdfFlagTarget: HTMLInputElement;
  declare hasRemovePdfFlagTarget: boolean;
  declare cropXTarget: HTMLInputElement;
  declare cropYTarget: HTMLInputElement;
  declare cropWTarget: HTMLInputElement;
  declare cropHTarget: HTMLInputElement;

  connect(): void {
    EB.TextEditor.init();

    const answerPicture = this.element.querySelector('#answer_picture, #answer_draft_picture');

    if (answerPicture) {
      answerPicture.addEventListener('change', this.enableCropImage.bind(this));
    }

    this.answerFormTarget.addEventListener('turbo:submit-end', event => this.handleSubmitResponse(event));

    document.addEventListener('turbo:before-fetch-response', (event) => {
      const xFlag = event.detail.fetchResponse.response.headers.get('X-Flag');

      if (xFlag) {
        this.closeModal();
      }
    });
  }

  public async imageInputChange(event: Event): Promise<void> {
    const maxFileSize = parseInt(this.imageInputTarget.dataset.maxFileSize || '0', 10);

    const file = (event.target as HTMLInputElement).files?.[0];

    if (!file) {
      this.unsupportedFormatHelpTextTarget.classList.add('hide');
      return;
    }

    const mimeType = await this.detectImageMimeType(file);

    if (!mimeType) {
      this.unsupportedFormatHelpTextTarget.classList.remove('hide');
      this.imageInputTarget.value = '';
      return;
    }

    this.unsupportedFormatHelpTextTarget.classList.add('hide');

    const imageHelpText = document.querySelector<HTMLElement>('.image-help-text');

    if (!imageHelpText) {
      return;
    }

    if (file.size > maxFileSize) {
      imageHelpText.style.color = 'red';
      this.imageInputTarget.value = '';
    }
    else {
      imageHelpText.style.color = '';
    }
  }

  private detectImageMimeType(file: File): Promise<string | null> {
    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.onloadend = (e: ProgressEvent<FileReader>) => {
        const bytes = new Uint8Array(e.target?.result as ArrayBuffer);

        const match = Object.entries(IMAGE_SIGNATURES).find(([, signature]) =>
          signature.every((byte, index) => bytes[index] === byte)
        );

        resolve(match ? match[0] : null);
      };

      reader.onerror = () => resolve(null);

      reader.readAsArrayBuffer(file.slice(0, 4));
    });
  }

  public toggleFullscreen(event: Event): void {
    const modal = document.querySelector('.turbo-modal-component__modal');
    const textElement = this.fullscreenButtonTarget.querySelector('span');
    event.preventDefault();

    if(modal.classList.contains('fullscreen')){
      modal.classList.remove('fullscreen');
      textElement.textContent = 'Fullscreen'
    }
    else{
      modal.classList.add('fullscreen');
      textElement.textContent = 'Exit Fullscreen'
    }
  }

  public closeModal(): void {
    const closeModalButton = document.querySelector('.turbo-modal-component__close-modal');

    if(closeModalButton) {
      closeModalButton.click();
    }
  }

  public enableCropImage(event: Event): void {
    const input = event.currentTarget as HTMLInputElement;

    if (window.FileReader && input) {
      if (!input.files || !input.files[0]) {
        return;
      }

      const reader = new FileReader();

      reader.onload = (e: ProgressEvent<FileReader>) => {
        const result = e.target?.result as string;
        const cropImageElement = document.querySelector('#crop_image');
        if (!cropImageElement) {
          return;
        }

        // remove existing croppie, if any
        this.removeCroppie(cropImageElement);

        // Remove any image tag and event listeners
        cropImageElement.innerHTML = '';

        // Initialize croppie
        const uploadCrop = new Croppie(cropImageElement, {
          viewport: {
            width: 100,
            height: 100,
            type: 'circle'
          },
          boundary: {
            width: 100,
            height: 120
          }
        });

        // Bind file upload image to croppie
        uploadCrop.bind({
          url: result,
          zoom: 0
        });

        // Listen for changes on crop to update coordinates

        uploadCrop.element.addEventListener('update', () => {
          this.updateCropCoordinates(uploadCrop);
        });
      };

      reader.readAsDataURL(input.files[0]);

      // Reveal help text
      this.updateText();
    } else {
      // Only upload the file to get the thumbnail if file reader is not supported
      this.submitImage();
    }
  }

  private removeCroppie(element: Element): void {
    const existingCroppie = element.dataset.croppie;
    if (existingCroppie) {
      existingCroppie.destroy();
    }
  }

  private updateCropCoordinates(element: Element): void {
    const points = element.get().points;
    this.cropXTarget.value = points[0];
    this.cropYTarget.value = points[1];
    // We are cropping a square, so witdh and height are the same
    this.cropWTarget.value = points[2] - points[0];
    this.cropHTarget.value = points[3] - points[1];
  }

  private updateText(): void {
    this.dragHelpTextTarget.classList.remove('hide');
    this.resizeHelpTextTarget.classList.remove('hide');
    this.uploadImageButtonTarget.textContent = I18n.t('pages.vote.ballot.question.nomination.profile.edit_image');
  }

  private submitImage(): void {
    if (this.answerFormTarget) {
      Rails.fire(this.answerFormTarget, 'submit');
    }
  }

  private changePdfInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const filename = input.files[0].name;
      this.pdfFileNameTarget.textContent = filename;
      this.pdfFileNameTarget.classList.remove('hide');
      this.uploadPdfButtonTarget.textContent = I18n.t('pages.vote.ballot.question.nomination.profile.edit_pdf');
      this.removePdfButtonTarget.classList.remove('hide');
      if (this.hasRemovePdfFlagTarget) {
        this.removePdfFlagTarget.value = '0';
      }
    }
  }

  // Client-side only: the PDF is purged on Save so the unsaved bio in the editor survives (CU-86e0b7x7h).
  public removePdf(event: Event): void {
    event.preventDefault();

    const message = (event.currentTarget as HTMLElement).dataset.confirmMessage;
    if (message && !window.confirm(message)) {
      return;
    }

    this.pdfInputTarget.value = '';
    this.pdfFileNameTarget.textContent = '';
    this.pdfFileNameTarget.classList.add('hide');
    this.removePdfButtonTarget.classList.add('hide');
    this.uploadPdfButtonTarget.textContent = I18n.t('pages.vote.ballot.question.nomination.profile.upload_pdf');
    if (this.hasRemovePdfFlagTarget) {
      this.removePdfFlagTarget.value = '1';
    }
  }

  private handleSubmitResponse(event): void {
    const modal = document.querySelector('.questions--answers--candidate-profile-modal');

    this.element.parentElement.removeAttribute("src");
    modal.remove();
  }

  private enableTurboForm(event: Event): void {
    this.syncQuillEditors();
    this.answerFormTarget.setAttribute('data-turbo', 'true');
  }

  private syncQuillEditors(): void {
    const editors = this.answerFormTarget.querySelectorAll('.rich_text_editor_component');

    editors.forEach((editor) => {
      const qlEditor = editor.querySelector('.ql-editor');
      const hiddenInput = editor.querySelector<HTMLInputElement>('input.rich_text_textarea');

      if (qlEditor && hiddenInput) {
        const content = qlEditor.innerHTML.replace(/(<p><br><\/p>)+$/, '');
        hiddenInput.value = content;
      }
    });
  }
}
